/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import { UserWithEmployee } from './UserSelectionModal';
import PerformanceVerbalWarningService from '../../../../services/PerformanceVerbalWarning';
import PriorDiscussionsTable from './PriorDiscussionsTable';
import { useVerbalWarningForm } from './VerbalWarning/Form/useVerbalWarningForm';
import { INITIAL_FORM_DATA } from './VerbalWarning/Form/formConstants';
import {
  UserInfoSection,
  PolicySection,
  ReasonForVerbalWarningSection,
  NextConsequencesSection,
  ExpectationOfImprovementSection,
  PrivateNotesSection,
} from './VerbalWarning/Form/FormSections';

interface VerbalWarningFormProps {
  isOpen: boolean;
  toggle: () => void;
  selectedUsers?: UserWithEmployee[];
  onSuccess?: () => void;
  editMode?: boolean;
  verbalWarningId?: string;
}

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForVerbalWarning: string;
  nextConsequences: string;
  expectationOfImprovement: string;
  privateNotes: string;
}

const VerbalWarningForm = ({
  isOpen,
  toggle,
  selectedUsers = [],
  onSuccess,
  editMode = false,
  verbalWarningId,
}: VerbalWarningFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const schoolId = currentSchool?.value;
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [submittedForms, setSubmittedForms] = useState<Map<string, FormData>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = selectedUsers[currentUserIndex];
  const isLastUser = currentUserIndex === selectedUsers.length - 1;
  const hasMultipleUsers = !editMode && selectedUsers.length > 1;

  const { formData, errors, policyViolations, priorDiscussions, handleInputChange, validateForm, setFormData } =
    useVerbalWarningForm(isOpen, token, currentUser, submittedForms, editMode, verbalWarningId);

  useEffect(() => {
    if (!isOpen) {
      setCurrentUserIndex(0);
      setSubmittedForms(new Map());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const saveCurrentForm = () => {
    if (currentUser) {
      const newSubmittedForms = new Map(submittedForms);
      newSubmittedForms.set(currentUser.id, { ...formData });
      setSubmittedForms(newSubmittedForms);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev + 1);
  };

  const handlePrevious = () => {
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent, approved: boolean = true) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(approved);
    } else {
      await submitDirectly(approved);
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(false);
    } else {
      await submitDirectly(false);
    }
  };

  const submitDirectly = async (approved: boolean) => {
    if (!validateForm()) return;
    saveCurrentForm();

    setIsSubmitting(true);
    try {
      const service = PerformanceVerbalWarningService(token);
      const finalForms = new Map(submittedForms);
      if (currentUser) {
        finalForms.set(currentUser.id, { ...formData });
      }

      const entries = Array.from(finalForms.entries());
      const verbalWarnings = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        date: formData.date,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        reasonForVerbalWarning: formData.reasonForVerbalWarning,
        nextConsequences: formData.nextConsequences,
        expectationOfImprovement: formData.expectationOfImprovement,
        privateNotes: formData.privateNotes,
        approved,
      }));

      await service.create({ schoolId, verbalWarnings });

      toast.success(
        approved
          ? `${
              verbalWarnings.length === 1
                ? 'Saved as pending for finalization'
                : `${verbalWarnings.length} Verbal Warnings saved as pending for finalization`
            }. Download the document, get it signed, then finalize from Pending Finalization.`
          : `${verbalWarnings.length === 1 ? 'Draft saved' : `${verbalWarnings.length} drafts saved`} successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting verbal warnings:', error);
      toast.error(error?.response?.data?.message || 'Error submitting verbal warnings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEditForm = async (approved: boolean = true) => {
    if (!verbalWarningId) return;

    setIsSubmitting(true);
    try {
      const service = PerformanceVerbalWarningService(token);
      const updateData = {
        schoolId,
        date: formData.date,
        policyViolationIds: formData.policyViolationIds,
        reasonForVerbalWarning: formData.reasonForVerbalWarning,
        nextConsequences: formData.nextConsequences,
        expectationOfImprovement: formData.expectationOfImprovement,
        privateNotes: formData.privateNotes,
        approved,
      };

      await service.update(verbalWarningId, updateData);

      toast.success(
        approved
          ? 'Verbal Warning submitted — check Pending Finalization to upload the signed document.'
          : 'Draft saved successfully!',
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error updating verbal warning:', error);
      toast.error(error?.response?.data?.message || 'Error updating verbal warning. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCurrentUserIndex(0);
    setSubmittedForms(new Map());
    setFormData(INITIAL_FORM_DATA);
    toggle();
  };

  if (selectedUsers.length === 0 && !editMode) {
    return (
      <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
        <ModalHeader toggle={handleCancel}>Verbal Warning</ModalHeader>
        <ModalBody>
          <p className="text-muted">Please select at least one employee to create a Verbal Warning form.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={handleCancel}>
        {editMode ? 'Edit Verbal Warning' : 'Verbal Warning'}
        {hasMultipleUsers && (
          <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
            ({currentUserIndex + 1} of {selectedUsers.length})
          </span>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit}>
          <UserInfoSection formData={formData} errors={errors} onChange={handleInputChange} />

          <PriorDiscussionsTable discussions={priorDiscussions} />

          <PolicySection
            formData={formData}
            errors={errors}
            policyViolations={policyViolations}
            onChange={handleInputChange}
          />

          <ReasonForVerbalWarningSection
            value={formData.reasonForVerbalWarning}
            error={errors.reasonForVerbalWarning}
            onChange={(value) => handleInputChange('reasonForVerbalWarning', value)}
          />

          <NextConsequencesSection
            value={formData.nextConsequences}
            error={errors.nextConsequences}
            onChange={(value) => handleInputChange('nextConsequences', value)}
          />

          <ExpectationOfImprovementSection
            value={formData.expectationOfImprovement}
            error={errors.expectationOfImprovement}
            onChange={(value) => handleInputChange('expectationOfImprovement', value)}
          />

          <PrivateNotesSection
            value={formData.privateNotes}
            onChange={(value) => handleInputChange('privateNotes', value)}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div className="ml-auto">
          {!editMode && hasMultipleUsers && currentUserIndex > 0 && (
            <Button color="outline-secondary" onClick={handlePrevious} className="mr-2">
              <i className="fas fa-arrow-left mr-2" />
              Previous
            </Button>
          )}
          {!editMode && hasMultipleUsers && !isLastUser ? (
            <Button color="primary" onClick={handleNext}>
              <i className="fas fa-arrow-right mr-2" />
              Next
            </Button>
          ) : (
            <>
              <Button color="warning" onClick={handleSaveDraft} disabled={isSubmitting} className="mr-2">
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" />
                    {hasMultipleUsers ? 'Review & Submit' : 'Submit'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default VerbalWarningForm;
